// Plausible analytics — cookieless, GDPR-friendly, no consent needed.
// Script is loaded from index.html. This module exposes a typed event helper.

type PlausibleProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: PlausibleProps; callback?: () => void }) => void;
  }
}

// AUD-26: Plausible script is injected at runtime only after analytics consent.
let plausibleLoaded = false;
export function loadPlausibleIfConsented(domain = "bootpreneurs.com") {
  if (plausibleLoaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    if (localStorage.getItem("bp_analytics_consent") !== "granted") return;
  } catch {
    return;
  }
  const s = document.createElement("script");
  s.defer = true;
  s.setAttribute("data-domain", domain);
  s.src = "https://plausible.io/js/script.js";
  document.head.appendChild(s);
  plausibleLoaded = true;
}

if (typeof window !== "undefined") {
  loadPlausibleIfConsented();
  window.addEventListener("storage", (e) => {
    if (e.key === "bp_analytics_consent" && e.newValue === "granted") loadPlausibleIfConsented();
  });
}

/**
 * Track a custom event in Plausible. No-op if the script hasn't loaded
 * (e.g., blocked by ad-blocker or running on localhost without `data-domain`).
 */
export function trackEvent(name: string, props?: PlausibleProps) {
  try {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      window.plausible(name, props ? { props } : undefined);
    }
  } catch {
    /* swallow — analytics must never break the app */
  }
}

// Convenience wrappers for the 4 wired events (typo-safe).
export const track = {
  signup: () => trackEvent("signup"),
  enrollClicked: (props: { product_id?: string; kind?: string }) =>
    trackEvent("enroll_clicked", props as PlausibleProps),
  paymentStarted: (props: { product_id?: string; amount?: number; currency?: string }) =>
    trackEvent("payment_started", props as PlausibleProps),
  paymentCompleted: (props: { order_id?: string; amount?: number; currency?: string }) =>
    trackEvent("payment_completed", props as PlausibleProps),
};
